<template>
  <div ref="el">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, useTemplateRef } from 'vue'

import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useIntersectionObserver } from '@vueuse/core'

import { useCompareTrackingContext } from '../../../composables/useCompareTracking/useCompareTracking'
import { SECTION_TRACKING_MAP } from '../../../utils/compareTracking'

// Tracking plan: ≥50% visible for ≥500ms.
const VISIBILITY_THRESHOLD = 0.5
const DWELL_TIME_MS = 500

const props = defineProps<{ sectionKey?: string }>()

const tracking = useTracking()
// Compare context is shared, SSR-safe state populated by the page (see
// `useCompareTracking`); reading it here keeps this component self-contained
// and avoids any provide/inject plumbing.
const compareContext = useCompareTrackingContext()
const el = useTemplateRef<HTMLElement>('el')

let dwellTimer: ReturnType<typeof setTimeout> | null = null
let fired = false

function trackDisplayed(sectionKey: string) {
  const config =
    SECTION_TRACKING_MAP[sectionKey as keyof typeof SECTION_TRACKING_MAP]
  if (!config) return

  tracking.trackCompareSectionDisplayed({
    ...(compareContext.value ?? {}),
    section_name: config.name,
    section_level: config.level,
    section_has_llm_text: config.hasLlmText,
  })
}

const { stop } = useIntersectionObserver(
  el,
  ([entry]) => {
    if (fired || !props.sectionKey) {
      return
    }

    if (entry.intersectionRatio < VISIBILITY_THRESHOLD) {
      if (dwellTimer) {
        clearTimeout(dwellTimer)
        dwellTimer = null
      }

      return
    }

    if (dwellTimer) {
      return
    }

    const { sectionKey } = props
    dwellTimer = setTimeout(() => {
      fired = true
      stop()
      trackDisplayed(sectionKey)
    }, DWELL_TIME_MS)
  },
  { threshold: VISIBILITY_THRESHOLD },
)

onBeforeUnmount(() => {
  if (dwellTimer) clearTimeout(dwellTimer)
})
</script>
